export type EventCategory = 'mommy-mingle' | 'kid-friendly' | 'mom-night-out' | 'festival' | 'community';
export type LocalEventTag = 'featured-partner' | 'mama-approved' | 'community-share';

export type MemberPerkType = 'early-access' | 'member-pricing' | 'member-only';

export interface MemberPerk {
  type: MemberPerkType;
  label: string;
}

export interface CalendarEvent {
  id: number;
  title: string;
  date: string; // YYYY-MM-DD
  time: string;
  endTime?: string;
  location: string;
  address: string;
  description: string;
  ageRecommendation: string;
  category: EventCategory;
  mapLink: string;
  image: string;
  rsvpLink?: string;
  featured?: boolean;
  localTag?: LocalEventTag;
  memberPerk?: MemberPerkType;
}

export const localTagConfig: Record<LocalEventTag, { label: string; color: string; bg: string; icon: string }> = {
  'featured-partner': {
    label: 'Featured Partner',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    icon: 'ri-star-fill',
  },
  'mama-approved': {
    label: 'Mama Approved',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    icon: 'ri-heart-fill',
  },
  'community-share': {
    label: 'Community Share',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    icon: 'ri-group-fill',
  },
};

export const categoryConfig: Record<EventCategory, { label: string; color: string; bg: string; border: string; dot: string }> = {
  'mommy-mingle': {
    label: 'Mommy Mingle',
    color: 'text-pink-700',
    bg: 'bg-pink-100',
    border: 'border-pink-300',
    dot: 'bg-pink-500',
  },
  'kid-friendly': {
    label: 'Kid Friendly',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    border: 'border-emerald-300',
    dot: 'bg-emerald-500',
  },
  'mom-night-out': {
    label: 'Mom Night Out',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    dot: 'bg-amber-500',
  },
  'festival': {
    label: 'Festival',
    color: 'text-orange-700',
    bg: 'bg-orange-100',
    border: 'border-orange-300',
    dot: 'bg-orange-500',
  },
  'community': {
    label: 'Community',
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    dot: 'bg-gray-500',
  },
};

export const memberPerkConfig: Record<MemberPerkType, { label: string; icon: string; description: string }> = {
  'early-access': {
    label: 'Early Access RSVP',
    icon: 'ri-vip-crown-line',
    description: 'Members get early access to RSVP before spots open to the public.',
  },
  'member-pricing': {
    label: 'Member Pricing',
    icon: 'ri-price-tag-3-line',
    description: 'Discounted pricing available exclusively for members.',
  },
  'member-only': {
    label: 'Members Only',
    icon: 'ri-lock-star-line',
    description: 'This event is exclusively available to Mommy Mingle members.',
  },
};

export const allEvents: CalendarEvent[] = [
  {
    id: 1,
    title: 'Mommy & Me Yoga in the Park',
    date: '2025-07-05',
    time: '10:00 AM',
    endTime: '11:30 AM',
    location: 'Tribble Mill Park',
    address: '3600 Tribble Mill Pkwy, Lawrenceville, GA 30045',
    description: 'Start your morning with a peaceful yoga session designed for moms and little ones. All levels welcome — bring a mat and your baby or toddler for a calming, community-filled morning.',
    ageRecommendation: 'Moms + babies/toddlers (0–4 yrs)',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=Tribble+Mill+Park+Lawrenceville+GA',
    image: 'https://public.readdy.ai/ai/img_res/b1b1edb43ddf896bc91d72ea954b22e1.jpg',
    featured: true,
    memberPerk: 'early-access',
  },
  {
    id: 2,
    title: 'Toddler Art & Craft Session',
    date: '2025-07-08',
    time: '2:00 PM',
    endTime: '3:30 PM',
    location: 'Sugar Hill Community Center',
    address: '5039 W Broad St, Sugar Hill, GA 30518',
    description: 'Let the little ones unleash their creativity! This guided art session is perfect for toddlers and preschoolers. All supplies provided. Moms are encouraged to join in the fun.',
    ageRecommendation: 'Ages 2–6 yrs',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=Sugar+Hill+Community+Center+GA',
    image: 'https://readdy.ai/api/search-image?query=toddlers%20doing%20colorful%20art%20and%20crafts%20with%20mothers%20bright%20creative%20workspace%20with%20paint%20supplies%20and%20craft%20materials%20joyful%20learning%20environment%20community%20activity%20warm%20lighting&width=800&height=500&seq=cal002&orientation=landscape',
    featured: true,
    memberPerk: 'member-pricing',
  },
  {
    id: 3,
    title: "Mom's Night Out: Wine & Paint",
    date: '2025-07-11',
    time: '7:00 PM',
    endTime: '9:30 PM',
    location: 'Painted Pin Buford',
    address: '4145 Buford Dr, Buford, GA 30518',
    description: 'Drop the kids off and treat yourself! Join fellow moms for a relaxing evening of painting, wine, and great conversation. No art experience needed — just good vibes.',
    ageRecommendation: 'Adults only (21+)',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=Painted+Pin+Buford+GA',
    image: 'https://readdy.ai/api/search-image?query=women%20painting%20on%20canvases%20in%20elegant%20studio%20setting%20with%20wine%20glasses%20sophisticated%20social%20gathering%20warm%20ambient%20lighting%20creative%20evening%20activity%20relaxed%20atmosphere&width=800&height=500&seq=cal003&orientation=landscape',
    featured: true,
    memberPerk: 'member-only',
  },
  {
    id: 4,
    title: 'Gwinnett Summer Splash Festival',
    date: '2025-07-12',
    time: '11:00 AM',
    endTime: '6:00 PM',
    location: 'Coolray Field',
    address: '2500 Buford Dr, Lawrenceville, GA 30043',
    description: 'A massive summer festival featuring water activities, live music, food trucks, and family entertainment. One of Gwinnett\'s biggest summer events — don\'t miss it!',
    ageRecommendation: 'All ages',
    category: 'festival',
    mapLink: 'https://maps.google.com/?q=Coolray+Field+Lawrenceville+GA',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20summer%20festival%20with%20families%20water%20activities%20live%20music%20food%20trucks%20colorful%20tents%20outdoor%20celebration%20sunny%20day%20community%20gathering%20festive%20atmosphere&width=800&height=500&seq=cal004&orientation=landscape',
    featured: true,
    localTag: 'featured-partner',
    memberPerk: 'member-pricing',
  },
  {
    id: 5,
    title: 'Playground Playdate Meetup',
    date: '2025-07-15',
    time: '10:00 AM',
    endTime: '12:00 PM',
    location: 'Vines Park',
    address: '3500 Oak Grove Rd, Loganville, GA 30052',
    description: 'Bring the kids and meet new mom friends! This casual outdoor playdate is a great way to connect with other local moms while the kids burn off energy at one of Gwinnett\'s best parks.',
    ageRecommendation: 'All ages welcome',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=Vines+Park+Loganville+GA',
    image: 'https://readdy.ai/api/search-image?query=children%20playing%20on%20modern%20playground%20equipment%20with%20mothers%20socializing%20nearby%20sunny%20outdoor%20setting%20community%20connection%20vibrant%20family%20friendly%20atmosphere%20green%20park&width=800&height=500&seq=cal005&orientation=landscape',
    memberPerk: 'early-access',
  },
  {
    id: 6,
    title: 'Sugar Hill Farmers Market',
    date: '2025-07-12',
    time: '8:00 AM',
    endTime: '12:00 PM',
    location: 'Sugar Hill City Hall',
    address: '5039 W Broad St, Sugar Hill, GA 30518',
    description: 'Shop fresh local produce, handmade goods, and artisan products at the weekly Sugar Hill Farmers Market. A wonderful Saturday morning tradition for the whole family.',
    ageRecommendation: 'All ages',
    category: 'kid-friendly',
    mapLink: 'https://maps.google.com/?q=Sugar+Hill+City+Hall+GA',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20farmers%20market%20with%20fresh%20produce%20and%20vendor%20stalls%20families%20shopping%20outdoors%20sunny%20morning%20atmosphere%20local%20community%20gathering%20colorful%20vegetables%20and%20flowers&width=800&height=500&seq=cal006&orientation=landscape',
    localTag: 'mama-approved',
  },
  {
    id: 7,
    title: 'Kids Story Time & Craft',
    date: '2025-07-17',
    time: '10:30 AM',
    endTime: '11:30 AM',
    location: 'Gwinnett County Public Library – Sugar Hill',
    address: '5039 W Broad St, Sugar Hill, GA 30518',
    description: 'Interactive story time followed by a themed craft activity. Perfect for little readers and curious minds. Free and open to all families in the community.',
    ageRecommendation: 'Ages 2–8 yrs',
    category: 'kid-friendly',
    mapLink: 'https://maps.google.com/?q=Gwinnett+County+Public+Library+Sugar+Hill+GA',
    image: 'https://readdy.ai/api/search-image?query=children%20sitting%20in%20circle%20listening%20to%20story%20time%20at%20library%20colorful%20books%20and%20reading%20area%20engaging%20educational%20environment%20warm%20welcoming%20atmosphere%20bright%20colors&width=800&height=500&seq=cal007&orientation=landscape',
    localTag: 'mama-approved',
  },
  {
    id: 8,
    title: 'Moms Who Brunch',
    date: '2025-07-19',
    time: '11:00 AM',
    endTime: '1:30 PM',
    location: 'The Brunch Spot Suwanee',
    address: '3770 Lawrenceville Suwanee Rd, Suwanee, GA 30024',
    description: 'A laid-back brunch gathering for moms to relax, recharge, and connect. Great food, great company, and zero mom guilt. Kids are welcome but this one\'s really for YOU.',
    ageRecommendation: 'Moms (kids welcome)',
    category: 'mom-night-out',
    mapLink: 'https://maps.google.com/?q=Suwanee+GA+brunch',
    image: 'https://readdy.ai/api/search-image?query=women%20enjoying%20brunch%20together%20at%20stylish%20restaurant%20bright%20airy%20interior%20mimosas%20and%20food%20on%20table%20laughing%20and%20connecting%20social%20gathering%20warm%20feminine%20atmosphere&width=800&height=500&seq=cal008&orientation=landscape',
    localTag: 'mama-approved',
    memberPerk: 'member-pricing',
  },
  {
    id: 9,
    title: 'Buford Balloon Festival',
    date: '2025-07-19',
    time: '5:00 PM',
    endTime: '10:00 PM',
    location: 'Buford City Park',
    address: '2200 Buford Hwy, Buford, GA 30518',
    description: 'Watch stunning hot air balloons light up the evening sky at this beloved annual festival. Food vendors, live entertainment, and a magical atmosphere the whole family will love.',
    ageRecommendation: 'All ages',
    category: 'festival',
    mapLink: 'https://maps.google.com/?q=Buford+City+Park+GA',
    image: 'https://readdy.ai/api/search-image?query=colorful%20hot%20air%20balloons%20glowing%20at%20dusk%20festival%20atmosphere%20families%20watching%20evening%20sky%20celebration%20outdoor%20event%20magical%20lighting%20community%20gathering&width=800&height=500&seq=cal009&orientation=landscape',
    localTag: 'featured-partner',
  },
  {
    id: 10,
    title: 'Community Cleanup Day',
    date: '2025-07-22',
    time: '9:00 AM',
    endTime: '12:00 PM',
    location: 'Sugar Hill Greenway Trail',
    address: 'Sugar Hill Greenway, Sugar Hill, GA 30518',
    description: 'Join neighbors and local families to help keep our community beautiful. Gloves and bags provided. A great way to teach kids about giving back while meeting your neighbors.',
    ageRecommendation: 'All ages (kids 5+ recommended)',
    category: 'community',
    mapLink: 'https://maps.google.com/?q=Sugar+Hill+Greenway+GA',
    image: 'https://readdy.ai/api/search-image?query=families%20and%20volunteers%20doing%20community%20cleanup%20outdoors%20picking%20up%20litter%20in%20park%20green%20environment%20teamwork%20community%20service%20sunny%20day%20positive%20atmosphere&width=800&height=500&seq=cal010&orientation=landscape',
    localTag: 'community-share',
  },
  {
    id: 11,
    title: 'Mommy Mingle Monthly Mixer',
    date: '2025-07-24',
    time: '6:30 PM',
    endTime: '9:00 PM',
    location: 'The Gathering Spot Duluth',
    address: '3280 Peachtree Corners Cir, Peachtree Corners, GA 30092',
    description: 'Our signature monthly mixer is back! Meet new moms, reconnect with old friends, enjoy light bites and drinks, and hear about upcoming Mommy Mingle events and opportunities.',
    ageRecommendation: 'Adults (18+)',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=Peachtree+Corners+GA',
    image: 'https://readdy.ai/api/search-image?query=women%20socializing%20at%20elegant%20indoor%20mixer%20event%20stylish%20venue%20with%20cocktails%20and%20appetizers%20warm%20lighting%20networking%20gathering%20feminine%20modern%20atmosphere%20community%20event&width=800&height=500&seq=cal011&orientation=landscape',
    featured: true,
    memberPerk: 'member-only',
  },
  {
    id: 12,
    title: 'Back to School Block Party',
    date: '2025-07-26',
    time: '3:00 PM',
    endTime: '7:00 PM',
    location: 'Suwanee Town Center',
    address: '330 Town Center Ave, Suwanee, GA 30024',
    description: 'Celebrate the end of summer with games, giveaways, food trucks, and fun for the whole family. Community-submitted event organized by local Suwanee families.',
    ageRecommendation: 'All ages',
    category: 'community',
    mapLink: 'https://maps.google.com/?q=Suwanee+Town+Center+GA',
    image: 'https://readdy.ai/api/search-image?query=neighborhood%20block%20party%20with%20families%20children%20playing%20games%20food%20trucks%20colorful%20decorations%20outdoor%20community%20celebration%20summer%20afternoon%20festive%20atmosphere&width=800&height=500&seq=cal012&orientation=landscape',
    localTag: 'community-share',
  },
  {
    id: 13,
    title: 'Fitness & Fun: Stroller Walk',
    date: '2025-07-29',
    time: '8:30 AM',
    endTime: '9:30 AM',
    location: 'George Pierce Park',
    address: '55 Buford Hwy, Suwanee, GA 30024',
    description: 'A casual stroller-friendly walk around one of Gwinnett\'s most beautiful parks. Great for new moms looking to get moving and meet other mamas in a relaxed, no-pressure setting.',
    ageRecommendation: 'Moms + babies/toddlers',
    category: 'mommy-mingle',
    mapLink: 'https://maps.google.com/?q=George+Pierce+Park+Suwanee+GA',
    image: 'https://readdy.ai/api/search-image?query=mothers%20walking%20with%20strollers%20in%20beautiful%20park%20morning%20light%20fitness%20walk%20community%20group%20outdoor%20exercise%20path%20trees%20and%20greenery%20healthy%20lifestyle&width=800&height=500&seq=cal013&orientation=landscape',
    memberPerk: 'early-access',
  },
  {
    id: 14,
    title: 'Gwinnett County Fair',
    date: '2025-07-31',
    time: '10:00 AM',
    endTime: '9:00 PM',
    location: 'Gwinnett County Fairgrounds',
    address: '2405 Sugarloaf Pkwy, Lawrenceville, GA 30045',
    description: 'Rides, games, funnel cakes, and live entertainment — the Gwinnett County Fair is a summer staple! Bring the whole family for a classic fair experience with something for everyone.',
    ageRecommendation: 'All ages',
    category: 'festival',
    mapLink: 'https://maps.google.com/?q=Gwinnett+County+Fairgrounds+GA',
    image: 'https://readdy.ai/api/search-image?query=county%20fair%20with%20colorful%20rides%20ferris%20wheel%20food%20stalls%20families%20enjoying%20carnival%20games%20festive%20atmosphere%20summer%20evening%20lights%20and%20excitement&width=800&height=500&seq=cal014&orientation=landscape',
    localTag: 'featured-partner',
  },
];
